// Day clock — the simple "check in / check out for the day" model for all staff
// (non-tech included). A day session is just a general time_entries row with no
// project. The server is authoritative for time: the `time_entries_enforce`
// trigger stamps started_at/ended_at and recomputes `seconds` from server time on
// every status transition, so the values sent here are ignored — we only send the
// status change. Attendance is stamped server-side by `time_entries_stamp_attendance`
// (no client write needed). See migration 20260614000000_time_integrity.sql.
import { supabase } from './supabase';
import { addDays, orgDateKey, orgToday } from './dates';
import { captureError } from './posthog';
import { write } from './write';

export type ClockEntry = {
  id: string;
  project_id: string | null;
  started_at: string;
  created_at: string;
  seconds: number;
  status: 'running' | 'paused' | 'logged';
};

export type ClockState = 'none' | 'in' | 'break' | 'out';

/**
 * How a clock action went. Every action used to resolve to nothing, so a refused
 * check-in and a successful one were the same promise: the card stayed on "Ready
 * when you are" and said so with confidence.
 *
 *   open_session — the insert hit `one_active_time_entry`: a session is already
 *                  open, today's (another tab) or one left over from an earlier day
 *   refused      — the session was closed elsewhere: the update matched no row,
 *                  or the server refused to reopen a logged one
 *   error        — anything else; `message` is the server's
 */
export type ClockResult =
  { ok: true } | { ok: false; reason: 'open_session' | 'refused' | 'error'; message: string };

const OK: ClockResult = { ok: true };

// Seconds counted so far for one entry — adds the live segment while running.
export const elapsedOf = (
  e: { status: string; seconds: number; started_at: string },
  nowMs: number,
): number =>
  e.status === 'running'
    ? e.seconds + Math.max(0, Math.floor((nowMs - new Date(e.started_at).getTime()) / 1000))
    : e.seconds;

// startOfToday() was browser midnight. "Today" is the ORG's day — the server
// stamps attendance.date from org_today() — so a viewer in another zone saw an
// empty clock for a day the server had already banked. docs/UI-CONVENTIONS.md §2.

const ENTRY = 'id, project_id, started_at, created_at, seconds, status';

// ALL of today's entries for the user (general day-clock AND project-timer
// sessions), oldest first. The one_active_time_entry index guarantees they
// never overlap, so the day clock can treat them as one stream of worked time —
// starting a project timer counts as being checked in.
//
// `null` is a read that failed — not an empty day. `data ?? []` turned an outage
// into "Ready when you are" (TS-14, UI-CONVENTIONS §7).
export async function loadDay(uid: string, tz: string): Promise<ClockEntry[] | null> {
  if (!supabase) return [];
  // Over-fetch from yesterday and filter by the org's calendar key. Deriving the
  // exact org-midnight instant from an IANA offset by hand is where these bugs
  // live; one extra day of rows is cheaper and provably right.
  const today = orgDateKey(Date.now(), tz);
  const { data, error } = await supabase
    .from('time_entries')
    .select(ENTRY)
    .eq('user_id', uid)
    .gte('created_at', addDays(orgToday(tz), -1).toISOString())
    .order('created_at', { ascending: true });
  if (error) return null;
  return ((data as ClockEntry[]) ?? []).filter((e) => orgDateKey(e.created_at, tz) === today);
}

/**
 * The session this person left open on an EARLIER org day, if there is one.
 *
 * loadDay reads today only, so a session from yesterday is invisible to it — and
 * `one_active_time_entry` still refuses the next check-in because of it. The
 * nightly sweeper closes these, but it selects on started_at, which a resume
 * moves to now(): a day paused and resumed after midnight is skipped until the
 * following night, and a night the sweep did not run skips everyone. At most one
 * row can match; the index guarantees it.
 *
 * `undefined` is a read that failed — not "none", which is `null`. Reading an
 * outage as none offered a Check in the index was bound to refuse (UI-CONVENTIONS §7).
 */
export async function loadStaleSession(
  uid: string,
  tz: string,
): Promise<ClockEntry | null | undefined> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('time_entries')
    .select(ENTRY)
    .eq('user_id', uid)
    .in('status', ['running', 'paused'])
    .limit(1);
  if (error) return undefined;
  const open = (data as ClockEntry[] | null)?.[0];
  return open && orgDateKey(open.created_at, tz) < orgDateKey(Date.now(), tz) ? open : null;
}

/**
 * Seconds of the person's OTHER logged day-clock time on the org day `s` began:
 * what the server takes off their contracted day when `s` is checked out late
 * (staleRecords). `null` is a read that failed.
 */
export async function loadLoggedOn(uid: string, tz: string, s: ClockEntry): Promise<number | null> {
  if (!supabase) return 0;
  // Over-fetched and keyed by the org's calendar, as loadDay is. ±2 days, not
  // ±1, so a 25-hour DST day stays inside the window.
  const at = new Date(s.created_at);
  const { data, error } = await supabase
    .from('time_entries')
    .select('id, created_at, seconds')
    .eq('user_id', uid)
    .is('project_id', null)
    .eq('status', 'logged')
    .gte('created_at', addDays(at, -2).toISOString())
    .lt('created_at', addDays(at, 2).toISOString());
  if (error) return null;
  const day = orgDateKey(s.created_at, tz);
  return ((data as Pick<ClockEntry, 'id' | 'created_at' | 'seconds'>[] | null) ?? [])
    .filter((e) => e.id !== s.id && orgDateKey(e.created_at, tz) === day)
    .reduce((t, e) => t + e.seconds, 0);
}

/**
 * What checking out of `s`, a session from an earlier org day, records. The
 * mirror of enforce_time_entry (the block marked 20261009230000), which caps it
 * as close_stale_sessions (20261009100000) does overnight: a day clock at the
 * contracted day less the day-clock time already `logged` that day, a project
 * timer at the plain day.
 */
export const staleRecords = (
  s: ClockEntry,
  perDaySec: number,
  logged: number,
  nowMs: number,
): number =>
  Math.min(elapsedOf(s, nowMs), s.project_id == null ? Math.max(0, perDaySec - logged) : perDaySec);

// Derived view of the day: who's clocked in, since when, and how long.
export function summarize(day: ClockEntry[], nowMs: number) {
  const active = day.find((e) => e.status === 'running' || e.status === 'paused') ?? null;
  const logged = day.filter((e) => e.status === 'logged');
  const totalSeconds =
    logged.reduce((s, e) => s + e.seconds, 0) + (active ? elapsedOf(active, nowMs) : 0);
  const first = day[0] ?? null;
  const state: ClockState = active
    ? active.status === 'running'
      ? 'in'
      : 'break'
    : logged.length
      ? 'out'
      : 'none';
  return { active, logged, totalSeconds, arrival: first?.created_at ?? null, state };
}

// Is the user on approved leave today? Used only to warn before they start the
// clock — working is never blocked. The server settles the day at check-out
// (reconcile_leave_clash); this now asks about the same calendar day the server
// will, instead of the device's.
export async function onApprovedLeaveToday(uid: string, tz: string): Promise<boolean> {
  if (!supabase) return false;
  const iso = orgDateKey(Date.now(), tz);
  const { data } = await supabase
    .from('leave_requests')
    .select('id')
    .eq('profile_id', uid)
    .eq('status', 'approved')
    .lte('start_date', iso)
    .gte('end_date', iso)
    .limit(1);
  return (data?.length ?? 0) > 0;
}

export async function checkIn(uid: string): Promise<ClockResult> {
  if (!supabase) return OK;
  // The enforce trigger sets started_at/seconds; we only declare the open day session.
  const { error } = await supabase.from('time_entries').insert({
    user_id: uid,
    project_id: null,
    task_id: null,
    status: 'running',
    is_billable: false,
  });
  if (!error) return OK;
  // 23505 is one_active_time_entry. It was swallowed here as "another tab has it
  // open, the refresh will show it" — true for today's session, and silence for
  // one left open on an earlier day, which no refresh of TODAY ever shows. The
  // caller re-reads and tells the two apart (loadStaleSession).
  if (error.code === '23505')
    return { ok: false, reason: 'open_session', message: 'You already have an open session.' };
  captureError(error, { op: 'dayclock.checkIn' });
  return { ok: false, reason: 'error', message: error.message };
}

// One status flip on an open session. It asks for the row back (lib/write): an
// update that matches nothing answers 204 with no error, so "took a break" on a
// session the sweeper had already closed looked exactly like one that worked.
async function flip(
  id: string,
  patch: { status: ClockEntry['status']; note?: string },
  op: string,
): Promise<ClockResult> {
  if (!supabase) return OK;
  const r = await write(supabase.from('time_entries').update(patch).eq('id', id));
  if (r.ok) return OK;
  // A session another tab or the overnight close already logged still matches
  // the id and passes RLS, so it is not zero rows: enforce_time_entry raises
  // this instead. It is the same answer, and an expected one, so not telemetry.
  const closed =
    r.reason === 'error' && r.error.message.includes('logged time entry cannot be modified');
  if (r.reason === 'refused' || closed)
    return {
      ok: false,
      reason: 'refused',
      message: 'That session is no longer open. It was closed somewhere else.',
    };
  // These writes used to discard `{ error }` entirely, so a rejected status flip
  // left the UI showing a state the server never took.
  captureError(r.error, { op, id });
  return { ok: false, reason: 'error', message: r.error.message };
}

// Pause bookkeeping — one time_entry_pauses row per pause, closed on resume,
// so HR can verify each break with its actual times (the entry itself only
// accumulates seconds and loses the boundaries). Written only once the flip it
// describes has landed: a pause row for a break that was refused is a false record.
async function logPause(entryId: string): Promise<void> {
  if (!supabase) return;
  await supabase.from('time_entry_pauses').insert({ entry_id: entryId });
}

async function closePause(entryId: string): Promise<void> {
  if (!supabase) return;
  await supabase
    .from('time_entry_pauses')
    .update({ resumed_at: new Date().toISOString() })
    .eq('entry_id', entryId)
    .is('resumed_at', null);
}

export async function takeBreak(e: ClockEntry): Promise<ClockResult> {
  // Trigger accumulates seconds + resets the segment; we only flip the status.
  const r = await flip(e.id, { status: 'paused' }, 'dayclock.takeBreak');
  if (r.ok) await logPause(e.id);
  return r;
}

export async function resumeClock(e: ClockEntry): Promise<ClockResult> {
  const r = await flip(e.id, { status: 'running' }, 'dayclock.resumeClock');
  if (r.ok) await closePause(e.id);
  return r;
}

// Check out: finalize the active session. The enforce trigger stamps ended_at and
// the final seconds; the attendance trigger writes today's hours server-side. An
// optional day summary rides the SAME update (a logged row is then immutable to
// its owner, so the note must be written as it transitions to 'logged').
//
// A failed check-out is the worst of the three to lose: the day never closes and
// attendance is never stamped. It reaches the caller now, not only telemetry, so
// the card can say so instead of flipping to "out".
export async function checkOut(active: ClockEntry, note?: string): Promise<ClockResult> {
  const n = note?.trim();
  // The note only when non-empty; blank = plain checkout.
  const r = await flip(
    active.id,
    n ? { status: 'logged', note: n } : { status: 'logged' },
    'dayclock.checkOut',
  );
  if (r.ok) await closePause(active.id); // checking out while on break ends that break
  return r;
}

// fmtClock lived here: m:ss under an hour, so "0:10" was ten seconds on this card
// and ten minutes in the correction beside it (TS-03). A duration is
// fmtDuration(s, 'clock' | 'hm' | 'decimal') from lib/duration.

/** Clock time of an instant in the ORG's zone — a 09:30 IST arrival read 04:00 to
 *  a UK viewer while the same row's date was org-local. */
export const fmtTime = (iso: string | null, tz?: string): string =>
  iso
    ? new Date(iso).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        ...(tz ? { timeZone: tz } : {}),
      })
    : '—';
